import { useState, useEffect } from "react";
import { KeyRound, Copy, Check, Sparkles, Loader2, RefreshCw, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Section } from "./Section";

export function LicenseGenerator({ t, input, open, onToggle }) {
  const [canGen, setCanGen] = useState(null);
  const [form, setForm] = useState({ company: "", expires: "", olt: "", clients: "", phone_interval: "7", client_email: "", renew_days: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [issued, setIssued] = useState([]);
  const [copiedId, setCopiedId] = useState("");
  const [hwReq, setHwReq] = useState(null);

  const onFpreq = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      try {
        const j = JSON.parse(r.result);
        if (!j.fingerprint) throw new Error("no fp");
        setHwReq(j);
        toast.success(t("Wczytano plik aktywacyjny"));
      } catch { toast.error(t("Nieprawidłowy plik .fpreq")); }
    };
    r.readAsText(f);
    e.target.value = "";
  };

  const loadIssued = async () => {
    try { const { data } = await api.get("/license/issued"); setIssued(Array.isArray(data) ? data : []); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    if (open && canGen === null) {
      api.get("/license/can-generate")
        .then(({ data }) => { setCanGen(!!data.can_generate); if (data.can_generate) loadIssued(); })
        .catch(() => setCanGen(false));
    }
    // eslint-disable-next-line
  }, [open]);

  const generate = async () => {
    if (!form.company.trim()) { toast.error(t("Podaj nazwę firmy/klienta")); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/license/generate", {
        company: form.company.trim(),
        expires: form.expires.trim(),
        olt: Number(form.olt) || 0,
        clients: Number(form.clients) || 0,
        phone_interval: Number(form.phone_interval) || 0,
        client_email: form.client_email.trim(),
        renew_days: Number(form.renew_days) || 0,
        hw: hwReq?.fingerprint || "",
        machine: hwReq || null,
      });
      setResult(data);
      toast.success(t("Licencja wygenerowana"));
      loadIssued();
    } catch (e) {
      toast.error(formatApiError ? formatApiError(e) : t("Nie udało się wygenerować licencji"));
    } finally { setBusy(false); }
  };

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      if (id) { setCopiedId(id); setTimeout(() => setCopiedId(""), 1800); }
      else { setCopied(true); setTimeout(() => setCopied(false), 1800); }
      toast.success(t("Skopiowano klucz"));
    } catch { toast.error(t("Nie udało się skopiować")); }
  };

  const renew = async (it) => {
    const d = window.prompt(t("Nowa data ważności (RRRR-MM-DD), puste = bezterminowa:"), it.expires || "");
    if (d === null) return;
    try {
      const { data } = await api.post(`/license/${it.id}/renew`, { expires: (d || "").trim() });
      setResult(data);
      toast.success(t("Wygenerowano odnowiony klucz — skopiuj i wyślij klientowi"));
      loadIssued();
    } catch (e) { toast.error(formatApiError ? formatApiError(e) : t("Błąd odnowienia")); }
  };

  const revoke = async (it) => {
    if (!window.confirm(t("Oznaczyć licencję jako wygaszoną? (dotyczy rejestru u dostawcy — nie wyłącza zdalnie działającej instalacji)"))) return;
    try {
      await api.post(`/license/${it.id}/revoke`);
      toast.success(t("Oznaczono jako wygaszoną"));
      loadIssued();
    } catch (e) { toast.error(formatApiError ? formatApiError(e) : t("Błąd")); }
  };

  const exportList = async (fmt) => {
    try {
      const res = await api.get(`/license/issued/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf" ? "licencje_fiberpass.pdf" : "licencje_fiberpass.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("Nie udało się wyeksportować rejestru")); }
  };

  return (
    <Section icon={Sparkles} iconColor="text-amber-400" title={t("Generator licencji (dla klientów)")}
      open={open} onToggle={onToggle} testid="settings-sec-licgen">
      {canGen === false ? (
        <div className="text-[11px] text-slate-400 bg-slate-900/60 border border-slate-800 rounded p-3" data-testid="licgen-disabled">
          {t("Generowanie licencji jest wyłączone na tym serwerze. Ustaw zmienną środowiskową")} <code className="text-cyan-400">FIBERPASS_LICENSE_PRIVATE_KEY</code> {t("(klucz prywatny dostawcy), aby włączyć tę funkcję.")}
        </div>
      ) : canGen === null ? (
        <div className="text-[11px] text-slate-500">{t("Sprawdzanie…")}</div>
      ) : (
        <>
          <p className="text-[11px] text-slate-400 mb-3">
            {t("Podpisz nowy klucz licencyjny (Ed25519) dla klienta. Klucz prywatny nigdy nie opuszcza tego serwera.")}
          </p>
          <div className="mb-3 bg-slate-900/60 border border-slate-800 rounded p-2.5" data-testid="licgen-fpreq-box">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-300">
                {hwReq
                  ? <span data-testid="licgen-hw-loaded">🔒 {t("Przypięte do sprzętu")}: <span className="font-mono text-cyan-400">{String(hwReq.fingerprint).slice(0, 16)}…</span> {hwReq.os ? `(${hwReq.os})` : ""}</span>
                  : t("Wgraj plik .fpreq od klienta, aby przypiąć licencję do jego sprzętu (opcjonalne).")}
              </div>
              <label className="shrink-0 text-[11px] bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 cursor-pointer" data-testid="licgen-fpreq-btn">
                {t("Wgraj .fpreq")}
                <input type="file" accept=".fpreq,application/json" onChange={onFpreq} className="hidden" />
              </label>
            </div>
            {hwReq && (
              <button type="button" onClick={() => setHwReq(null)} data-testid="licgen-hw-clear"
                className="mt-1 text-[10px] text-slate-500 hover:text-slate-300 underline">{t("Usuń przypięcie (licencja bez sprzętu)")}</button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Nazwa firmy / klienta")} *</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="ACME ISP Sp. z o.o." data-testid="licgen-company" className={input} />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("E-mail klienta (przypomnienia o wygasaniu)")}</label>
              <input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                placeholder="kontakt@klient.pl" data-testid="licgen-client-email" className={input} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Ważność do")}</label>
                <input type="date" value={form.expires} onChange={(e) => setForm({ ...form, expires: e.target.value })}
                  data-testid="licgen-expires" className={input} />
                <span className="text-[10px] text-slate-500">{t("puste = bezterminowa")}</span>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Limit OLT")}</label>
                <input type="number" min="0" value={form.olt} onChange={(e) => setForm({ ...form, olt: e.target.value })}
                  placeholder="0" data-testid="licgen-olt" className={input} />
                <span className="text-[10px] text-slate-500">{t("0 = bez limitu")}</span>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Limit klientów")}</label>
                <input type="number" min="0" value={form.clients} onChange={(e) => setForm({ ...form, clients: e.target.value })}
                  placeholder="0" data-testid="licgen-clients" className={input} />
                <span className="text-[10px] text-slate-500">{t("0 = bez limitu")}</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Interwał kontaktu z serwerem (dni)")}</label>
              <input type="number" min="0" value={form.phone_interval} onChange={(e) => setForm({ ...form, phone_interval: e.target.value })}
                placeholder="7" data-testid="licgen-phone-interval" className={input} />
              <span className="text-[10px] text-slate-500">{t("Zalecane 7 — klient musi połączyć się z serwerem dostawcy min. raz na 7 dni (z zapasem 3 dni), inaczej program się zablokuje.")}</span>
              {String(form.phone_interval).trim() === "0" && (
                <div className="mt-1 text-[11px] text-amber-300 bg-amber-950/30 border border-amber-800 rounded px-2 py-1" data-testid="licgen-offline-warn">
                  {t("Uwaga: 0 = licencja działa offline bez ograniczeń. Odcięcie internetu nie zablokuje programu i nie zadziała zdalne odwołanie licencji.")}
                </div>
              )}
            </div>
            <div className="border-t border-slate-800 pt-2">
              <label className="text-xs font-semibold text-slate-300 mb-1 block">{t("Domyślny okres przedłużenia (dni)")}</label>
              <input type="number" min="0" value={form.renew_days} onChange={(e) => setForm({ ...form, renew_days: e.target.value })}
                placeholder="365" data-testid="licgen-renew-days" className={input} />
              <span className="text-[10px] text-slate-500">{t("Podpowiedź, o ile dni przedłużać przy zatwierdzaniu prośby klienta. Odnowienie zawsze zatwierdza dostawca (po opłacie).")}</span>
            </div>
          </div>
          <button type="button" onClick={generate} disabled={busy} data-testid="licgen-submit"
            className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded px-3 py-2 text-sm font-medium">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} {t("Generuj licencję")}
          </button>

          {result && (
            <div className="mt-3 bg-slate-900/70 border border-amber-800/50 rounded p-3" data-testid="licgen-result">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-amber-300">{t("Wygenerowany klucz")}</span>
                <button type="button" onClick={() => copyText(result.key)} data-testid="licgen-copy"
                  className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300">
                  {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? t("Skopiowano") : t("Kopiuj")}
                </button>
              </div>
              <div className="font-mono text-[10px] text-slate-200 break-all bg-slate-950 rounded p-2 border border-slate-800" data-testid="licgen-key">
                {result.key}
              </div>
              <div className="text-[11px] text-slate-400 mt-2 space-y-0.5">
                <div>{t("Firma")}: <span className="text-slate-200">{result.info?.company}</span></div>
                <div>{t("Ważność")}: <span className="text-slate-200">{result.info?.expires || t("bezterminowa")}</span></div>
                {result.info?.limits && Object.keys(result.info.limits).length > 0 && (
                  <div>{t("Limity")}: <span className="text-slate-200">{Object.entries(result.info.limits).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span></div>
                )}
                {result.info?.phone_interval ? (
                  <div>{t("Kontakt z serwerem")}: <span className="text-slate-200">{t("min. raz na")} {result.info.phone_interval} {t("dni")}</span></div>
                ) : (
                  <div>{t("Kontakt z serwerem")}: <span className="text-slate-200">{t("offline (niewymagany)")}</span></div>
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-2">{t("Przekaż ten klucz klientowi — wkleja go w Ustawienia → Marka → Klucz licencyjny.")}</p>
            </div>
          )}

          <div className="mt-4 border-t border-slate-700 pt-3" data-testid="licgen-issued">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-300">
                {t("Wystawione licencje")} <span className="text-slate-500">({issued.length})</span>
              </div>
              {issued.length > 0 && (
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => exportList("csv")} data-testid="licgen-export-csv"
                    className="text-[11px] bg-slate-700 hover:bg-slate-600 rounded px-2 py-1">CSV</button>
                  <button type="button" onClick={() => exportList("pdf")} data-testid="licgen-export-pdf"
                    className="text-[11px] bg-slate-700 hover:bg-slate-600 rounded px-2 py-1">PDF</button>
                </div>
              )}
            </div>
            {issued.length === 0 ? (
              <div className="text-[11px] text-slate-500">{t("Brak wystawionych licencji.")}</div>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-auto pr-1">
                {issued.map((it) => {
                  const lim = it.limits && Object.keys(it.limits).length
                    ? Object.entries(it.limits).map(([k, v]) => `${k}:${v}`).join(" ")
                    : t("bez limitu");
                  return (
                    <div key={it.id} data-testid={`licgen-row-${it.id}`}
                      className={`flex items-center gap-2 rounded px-2.5 py-2 border ${it.revoked ? "bg-red-950/20 border-red-900/50 opacity-70" : "bg-slate-900/60 border-slate-800"}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-200 truncate">
                          {it.company}
                          {it.hw ? <span className="ml-1 text-[9px] text-cyan-500">🔒 sprzęt</span> : null}
                          {it.revoked ? <span className="ml-1 text-[9px] text-red-400">WYGASZONA</span> : null}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {(it.created_at || "").slice(0, 10)} · {t("do")}: {it.expires || t("bezterminowa")} · {lim}
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <button type="button" onClick={() => copyText(it.key, it.id)} data-testid={`licgen-copy-${it.id}`}
                          title={t("Kopiuj klucz")}
                          className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300">
                          {copiedId === it.id ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                        <button type="button" onClick={() => renew(it)} data-testid={`licgen-renew-${it.id}`}
                          title={t("Odnów (nowy klucz z nową datą)")}
                          className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:text-emerald-300">
                          <RefreshCw size={12} />
                        </button>
                        {!it.revoked && (
                          <button type="button" onClick={() => revoke(it)} data-testid={`licgen-revoke-${it.id}`}
                            title={t("Wygaś (rejestr)")}
                            className="inline-flex items-center gap-1 text-[11px] text-red-400 hover:text-red-300">
                            <Ban size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </Section>
  );
}
